import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import {
  ClockCircleOutlined,
  CheckOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ReactComponent as LogoImage } from '../../images/logo-full.svg';
import {
  getPendingEmailImport,
  attachPendingEmailImport,
} from '../../services/api';
import {
  ApplicationStatus,
  AttachPendingEmailResponse,
  PendingEmailApplication,
  PendingEmailImport,
} from '../../types';
import { buildSigninUrl } from '../../utils/redirectUtils';
import { isTokenExpired } from '../../utils/authUtils';
import { captureEvent } from '../../services/analytics';
import css from './EmailImportAttach.module.scss';

type Status =
  | 'loading'
  | 'expired'
  | 'picker'
  | 'empty'
  | 'attaching'
  | 'attached'
  | 'error';

const statusDisplayNames: Record<ApplicationStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  screening_call: 'Screening Call',
  first_interview: 'First Interview',
  second_interview: 'Second Interview',
  additional_interview: 'Additional Interview',
  technical_assessment: 'Technical Assessment',
  reference_check: 'Reference Check',
  offer_extended: 'Offer Extended',
  negotiation: 'Negotiation',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date} at ${time}`;
};

const checkLoggedIn = (): boolean => {
  const t = localStorage.getItem('accessToken');
  const exp = localStorage.getItem('accessTokenExpiration');
  if (!t || !exp) return false;
  return !isTokenExpired(exp);
};

const EmailImportAttach: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<Status>('loading');
  const [pending, setPending] = useState<PendingEmailImport | null>(null);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [result, setResult] = useState<AttachPendingEmailResponse | null>(null);
  const [showFullEmail, setShowFullEmail] = useState(false);

  const loggedIn = useMemo(() => checkLoggedIn(), []);
  const userFirstName = localStorage.getItem('userFirstName');

  const loadPending = (currentToken: string) => {
    setStatus('loading');
    getPendingEmailImport(currentToken)
      .then((data) => {
        setPending(data);
        setStatus(data.applications.length === 0 ? 'empty' : 'picker');
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setStatus('expired');
        } else {
          setStatus('error');
        }
      });
  };

  useEffect(() => {
    if (!token) {
      setStatus('expired');
      return;
    }
    loadPending(token);
  }, [token]);

  const handleAttach = async () => {
    if (!selectedAppId || !token) return;
    setStatus('attaching');
    try {
      const res = await attachPendingEmailImport(token, selectedAppId);
      setResult(res);
      setStatus('attached');
      captureEvent('forwarded_email_attached', {
        status_changed: res.statusChanged,
        is_duplicate: res.status === 'duplicate',
        to_status: res.newStatus ?? null,
      });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
    }
  };

  const handleViewApplication = () => {
    if (!selectedAppId) return;
    const target = `/applications/${selectedAppId}`;
    navigate(loggedIn ? target : buildSigninUrl(target));
  };

  const handleGoHome = () => {
    navigate(loggedIn ? '/' : buildSigninUrl('/'));
  };

  const handleSeeOpportunities = () => {
    navigate(loggedIn ? '/opportunities' : buildSigninUrl('/opportunities'));
  };

  const handleSignIn = () => {
    const next = `/email-imports/attach${
      token ? `?token=${encodeURIComponent(token)}` : ''
    }`;
    navigate(buildSigninUrl(next));
  };

  const handleRetry = () => {
    if (token) {
      loadPending(token);
    }
  };

  const selectedApp =
    pending?.applications.find((a) => a.applicationId === selectedAppId) ??
    null;

  return (
    <div className={css.page}>
      <header className={css.header}>
        <a
          href="https://jobbeast.org"
          target="_blank"
          rel="noopener noreferrer"
          className={css.logoLink}
          aria-label="Job Beast"
        >
          <LogoImage className={css.logo} />
        </a>
        <div className={css.authPill}>
          {loggedIn ? (
            <span className={css.authIdent}>
              <span className={css.authDot} aria-hidden="true" />
              Signed in{userFirstName ? ` as ${userFirstName}` : ''}
            </span>
          ) : (
            <button className={css.authSignin} onClick={handleSignIn}>
              Sign in
            </button>
          )}
        </div>
      </header>

      <main className={css.main}>
        <article className={css.card} aria-busy={status === 'loading' || status === 'attaching'}>
          {status === 'loading' && (
            <div className={css.centered}>
              <Spin size="large" />
              <p className={css.centeredText}>Loading your forwarded email…</p>
            </div>
          )}

          {status === 'expired' && (
            <div className={css.centered}>
              <div className={css.iconCircle}>
                <ClockCircleOutlined />
              </div>
              <h1 className={css.heading}>This link has expired</h1>
              <p className={css.copy}>
                Or it has already been used. Forward the email again to get a
                fresh link.
              </p>
              <div className={css.actions}>
                <button className={css.primary} onClick={handleGoHome}>
                  {loggedIn ? 'Go to dashboard' : 'Sign in to Job Beast'}
                  <ArrowRightOutlined className={css.arrow} />
                </button>
                <a
                  className={css.secondary}
                  href="https://www.jobbeast.org/email-integration/manual-forward"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  How manual forwarding works →
                </a>
              </div>
            </div>
          )}

          {status === 'empty' && (
            <div className={css.centered}>
              <div className={css.iconCircle}>
                <InboxOutlined />
              </div>
              <h1 className={css.heading}>No applications to attach this to</h1>
              <p className={css.copy}>
                {pending ? (
                  <>
                    We received your forward of{' '}
                    <strong>{pending.subject}</strong>, but you don't have any
                    active applications yet — there's nothing to attach this
                    reply to.
                  </>
                ) : (
                  "You don't have any active applications yet — apply to something first, then forward replies."
                )}
              </p>
              <div className={css.actions}>
                <button className={css.primary} onClick={handleSeeOpportunities}>
                  {loggedIn ? 'See opportunities' : 'Sign in to Job Beast'}
                  <ArrowRightOutlined className={css.arrow} />
                </button>
              </div>
            </div>
          )}

          {(status === 'picker' || status === 'attaching') && pending && (
            <div className={css.picker}>
              <div className={css.heroLead}>
                <span className={css.kicker}>Forwarded email</span>
                <h1 className={css.heroHeading}>
                  Pick the application this reply belongs to
                </h1>
                <p className={css.heroCopy}>
                  We couldn't automatically match this forward. Once you choose,
                  we'll attach it and update the application's status if
                  anything changed.
                </p>
              </div>

              <section className={css.summary}>
                <dl className={css.summaryRows}>
                  <div className={css.summaryRow}>
                    <dt>From</dt>
                    <dd className={css.summaryMono}>{pending.sender}</dd>
                  </div>
                  <div className={css.summaryRow}>
                    <dt>Subject</dt>
                    <dd>{pending.subject}</dd>
                  </div>
                  <div className={css.summaryRow}>
                    <dt>Received</dt>
                    <dd>{formatDateTime(pending.receivedAt)}</dd>
                  </div>
                </dl>
                <details
                  className={css.bodyDetails}
                  open={showFullEmail}
                  onToggle={(e) =>
                    setShowFullEmail((e.target as HTMLDetailsElement).open)
                  }
                >
                  <summary className={css.bodySummary}>
                    {showFullEmail ? 'Hide email' : 'Show full email'}
                  </summary>
                  <pre className={css.bodyPre}>{pending.body}</pre>
                </details>
              </section>

              <h2 className={css.pickerHeading}>Your active applications</h2>
              <div
                className={css.appList}
                role="radiogroup"
                aria-label="Pick an application"
              >
                {pending.applications.map((app) => (
                  <ApplicationOption
                    key={app.applicationId}
                    app={app}
                    selected={selectedAppId === app.applicationId}
                    onSelect={() => setSelectedAppId(app.applicationId)}
                  />
                ))}
              </div>

              <div className={css.actionBar}>
                <button
                  type="button"
                  className={css.cancel}
                  onClick={handleGoHome}
                  disabled={status === 'attaching'}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={css.primary}
                  onClick={handleAttach}
                  disabled={!selectedAppId || status === 'attaching'}
                >
                  {status === 'attaching' ? (
                    <>
                      Attaching… <Spin size="small" />
                    </>
                  ) : (
                    <>
                      Attach Email <ArrowRightOutlined className={css.arrow} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {status === 'attached' && result && selectedApp && (
            <div className={css.centered}>
              <div className={`${css.iconCircle} ${css.iconCircleSuccess}`}>
                <CheckOutlined />
              </div>
              <h1 className={css.heading}>
                {result.status === 'duplicate'
                  ? 'Already attached'
                  : 'Email attached'}
              </h1>
              <p className={css.copy}>
                {result.statusChanged && result.newStatus ? (
                  <>
                    {selectedApp.companyName} — {selectedApp.roleTitle}. Status
                    updated:{' '}
                    <strong>
                      {statusDisplayNames[selectedApp.applicationStatus]}
                    </strong>{' '}
                    →{' '}
                    <strong>{statusDisplayNames[result.newStatus]}</strong>.
                  </>
                ) : (
                  <>
                    {selectedApp.companyName} — {selectedApp.roleTitle}. The
                    reply is now on this application.
                  </>
                )}
              </p>
              <div className={css.actions}>
                <button className={css.primary} onClick={handleViewApplication}>
                  View application
                  <ArrowRightOutlined className={css.arrow} />
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className={css.centered}>
              <div className={`${css.iconCircle} ${css.iconCircleError}`}>
                <ExclamationCircleOutlined />
              </div>
              <h1 className={css.heading}>Something went wrong</h1>
              <p className={css.copy}>
                We couldn't process this just now. Try again in a moment, or
                forward the email again to get a fresh link.
              </p>
              <div className={css.actions}>
                <button className={css.primary} onClick={handleRetry}>
                  Try again
                </button>
              </div>
            </div>
          )}
        </article>
      </main>
    </div>
  );
};

interface ApplicationOptionProps {
  app: PendingEmailApplication;
  selected: boolean;
  onSelect: () => void;
}

const ApplicationOption: React.FC<ApplicationOptionProps> = ({
  app,
  selected,
  onSelect,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    className={`${css.appOption} ${selected ? css.appOptionSelected : ''}`}
    onClick={onSelect}
  >
    <span className={css.appIndicator} aria-hidden="true" />
    <div className={css.appBody}>
      <div className={css.appTopRow}>
        <span className={css.appCompany}>{app.companyName}</span>
        <span className={css.appStatus}>
          {statusDisplayNames[app.applicationStatus]}
        </span>
      </div>
      <span className={css.appRole}>{app.roleTitle}</span>
      <div className={css.appMeta}>
        <span className={css.appMetaItem}>
          <CalendarOutlined /> Applied {formatDate(app.submittedAt)}
        </span>
        {app.contactName && (
          <span className={css.appMetaItem}>
            {app.contactName}
            {app.contactEmail ? ` · ${app.contactEmail}` : ''}
          </span>
        )}
      </div>
    </div>
  </button>
);

export default EmailImportAttach;
